import React, { useState, forwardRef, useImperativeHandle } from "react"
import { Grid, Tooltip } from "@material-ui/core"
import { InfoOutlined as TooltipInfoIcon } from "@material-ui/icons"

import SummarizeAttendanceButton from "@/components/Skynet/AttendanceSummary/SummarizeAttendanceButton"
import AttendanceSummaryModal from "@/components/Skynet/AttendanceSummary/NotificationMessage/AttendanceSummaryFooter/AttendanceSummaryModal"
import AttendanceSummaryUpsellDialog from "@/components/Skynet/AttendanceSummary/Upsell/AttendanceSummaryUpsellDialog"

import {
	isAttendanceFinishedNotificationMessage,
	isRelevantAttendanceNotificationMessage,
	SUMMARY_INFO_TOOLTIP_TEXT,
	CHIP_TEXT_FOR_USER_WITHOUT_IA,
	CHIP_TEXT_WHEN_SUMMARY_EXISTS,
	CHIP_TEXT_WHEN_NO_SUMMARY
} from "@/utils/attendanceSummary"

import { Message } from "@/protocols/chatGlobalStateProtocol"
import { UserInInstanceRoleName } from "@/protocols/userInInstance"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import useCustomMemo from "@/hooks/useCustomMemo"

import useStyles from "@/components/Skynet/AttendanceSummary/NotificationMessage/AttendanceSummaryFooter/styles"

export type AttendanceSummaryFooterHandler = {
	checkIfIsRenderingFullChip: () => boolean
}

type AttendanceSummaryFooterProps = {
	message: Message
	hasAIResourcesPermission: boolean
	userRoleName: UserInInstanceRoleName
}

const AttendanceSummaryFooter: React.ForwardRefRenderFunction<AttendanceSummaryFooterHandler, AttendanceSummaryFooterProps> = (props, ref) => {
	const {
		message,
		hasAIResourcesPermission,
		userRoleName
	} = props

	const classes = useStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()

	const inboxChannelChatAttendanceId = message.extraData?.notificationInfo?.emittedAtInboxChannelChatAttendanceId ?? 0

	const isLastRelevantAttendanceNotificationMessage = useCustomMemo(() => {
		const relevantAttendanceNotificationMessagesOfSameProtocol = chatGlobalStateStore.message.current
			.filter(isRelevantAttendanceNotificationMessage)
			.filter((attendanceNotificationMessage) => (attendanceNotificationMessage.extraData?.notificationInfo?.emittedAtInboxChannelChatAttendanceId ?? 0) === inboxChannelChatAttendanceId)
		const lastRelevantAttendanceNotificationMessage = relevantAttendanceNotificationMessagesOfSameProtocol[relevantAttendanceNotificationMessagesOfSameProtocol.length - 1]

		return lastRelevantAttendanceNotificationMessage?.id === message.id
	}, [chatGlobalStateStore.chat.current?.id, chatGlobalStateStore.message.currentHash, inboxChannelChatAttendanceId, message.id])

	const hasSummaryFromAnyAttendanceNotificationMessage = useCustomMemo(() => {
		return chatGlobalStateStore.message.current.some(currentMessage => {
			const currentMessageInboxChannelChatAttendanceId = currentMessage.extraData?.notificationInfo?.emittedAtInboxChannelChatAttendanceId ?? 0
			const isFromSameAttendance = currentMessageInboxChannelChatAttendanceId === inboxChannelChatAttendanceId
			const hasAttendanceSummaryGeneratedByAI = Boolean(currentMessage.extraData?.notificationInfo?.hasAttendanceSummaryGeneratedByAI)

			return isFromSameAttendance && hasAttendanceSummaryGeneratedByAI
		})
	}, [chatGlobalStateStore.message.currentHash, inboxChannelChatAttendanceId])

	const [hasSummaryOptimistic, setHasSummaryOptimistic] = useState<boolean>(false)
	const [isChipDismissed, setIsChipDismissed] = useState<boolean>(false)

	const hasSummary = hasSummaryFromAnyAttendanceNotificationMessage || hasSummaryOptimistic

	if (!inboxChannelChatAttendanceId) {
		return null
	}

	const shouldShowFullChip = isLastRelevantAttendanceNotificationMessage && (hasAIResourcesPermission || !isChipDismissed)
	const isChipPulsing = isLastRelevantAttendanceNotificationMessage && isAttendanceFinishedNotificationMessage(message) && !hasSummary

	const contactName = chatGlobalStateStore.chat.current?.title ?? ""

	const chipLabel = !hasAIResourcesPermission
		? CHIP_TEXT_FOR_USER_WITHOUT_IA
		: hasSummary
			? CHIP_TEXT_WHEN_SUMMARY_EXISTS
			: CHIP_TEXT_WHEN_NO_SUMMARY

	const handleRequestSummary = () => {
		if (hasAIResourcesPermission) {
			AttendanceSummaryModal.open({
				inboxChannelChatAttendanceId,
				contactName,
				userRoleName,
				clientId: chatGlobalStateStore.chat.current?.client?.id ?? 0,
				inboxChannelChatId: chatGlobalStateStore.chat.current?.id ?? 0,
				onClose: () => {
					setHasSummaryOptimistic(true)
				}
			})
		} else {
			AttendanceSummaryUpsellDialog.open({
				userRoleName,
				source: "inbox"
			})
		}
	}

	const handleDismissChip = () => {
		setIsChipDismissed(true)
	}

	useImperativeHandle(ref, () => ({
		checkIfIsRenderingFullChip: () => shouldShowFullChip
	}))

	const showDeleteButton = !hasAIResourcesPermission && shouldShowFullChip

	if (shouldShowFullChip) {
		return (
			<Grid
				item
				xs={12}
				container
				justifyContent="center"
				alignItems="center"
				spacing={1}
			>
				<Grid item>
					<SummarizeAttendanceButton
						label={chipLabel}
						isPulsing={isChipPulsing}
						disabled={false}
						onClick={handleRequestSummary}
						onDelete={showDeleteButton ? handleDismissChip : undefined}
					/>
				</Grid>
				<Grid item>
					<Grid container>
						<Tooltip
							title={SUMMARY_INFO_TOOLTIP_TEXT}
							placement="top"
						>
							<TooltipInfoIcon
								className={classes.tooltipInfoIcon}
								color="primary"
							/>
						</Tooltip>
					</Grid>
				</Grid>
			</Grid>
		)
	}

	return (
		<Grid
			item
		>
			<Tooltip
				title={chipLabel}
				placement="top"
			>
				<Grid>
					<SummarizeAttendanceButton
						label={chipLabel}
						isCompact
						disabled={false}
						onClick={handleRequestSummary}
					/>
				</Grid>
			</Tooltip>
		</Grid>
	)
}

export default forwardRef(AttendanceSummaryFooter)
